import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import * as api from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { isAdmin } from '../../utils/auth'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import Spinner from '../Spinner/Spinner'
import { formatTND } from '../../utils/formatTND'
import {
  FISCAL_CATEGORY_OPTIONS,
  formatFiscalCategoryLabel,
  isAutoEntrepreneur,
} from '../../utils/fiscalCategory'
import styles from './Coaches.module.css'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const DEFAULT_PAGE_SIZE = 10
const DOC_IMPORT_BATCH_SIZE = 15

const DOSSIER_FILTER_OPTIONS = [
  { value: 'all', label: 'Tous les coaches' },
  { value: 'complete', label: 'Dossier renseigné' },
  { value: 'missing', label: 'Dossier à compléter' },
]

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'active', label: 'Actifs uniquement' },
  { value: 'inactive', label: 'Inactifs uniquement' },
]

const RETENUE_OPTIONS = [
  { value: 0.03, label: '3 % (0,03)' },
  { value: 0.1, label: '10 % (0,10)' },
]

const formatRetenueLabel = (value) => {
  const rate = Number(value)
  if (rate === 0.1) return '10 %'
  return '3 %'
}

const parseCoachesListQuery = (searchString = '') => {
  const params = new URLSearchParams(searchString)
  const page = Math.max(1, Number(params.get('page')) || 1)
  const limitRaw = Number(params.get('limit'))
  const pageSize = PAGE_SIZE_OPTIONS.includes(limitRaw) ? limitRaw : DEFAULT_PAGE_SIZE
  const search = params.get('search') || ''
  const dossierRaw = params.get('dossier') || 'all'
  const dossierFilter = DOSSIER_FILTER_OPTIONS.some((option) => option.value === dossierRaw)
    ? dossierRaw
    : 'all'
  const statusRaw = params.get('status') || 'all'
  const statusFilter = STATUS_FILTER_OPTIONS.some((option) => option.value === statusRaw)
    ? statusRaw
    : 'all'

  return { page, pageSize, search, dossierFilter, statusFilter }
}

const buildCoachesListQuery = ({ page, pageSize, search, dossierFilter, statusFilter }) => {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (pageSize !== DEFAULT_PAGE_SIZE) params.set('limit', String(pageSize))
  if (search.trim()) params.set('search', search.trim())
  if (dossierFilter !== 'all') params.set('dossier', dossierFilter)
  if (statusFilter !== 'all') params.set('status', statusFilter)
  const query = params.toString()
  return query ? `?${query}` : ''
}

const COACHES_LIST_RETURN_KEY = 'coaches-list-return'

const saveCoachesListReturnState = (coachId) => {
  sessionStorage.setItem(COACHES_LIST_RETURN_KEY, JSON.stringify({
    scrollY: window.scrollY,
    highlightCoachId: coachId,
  }))
}

const SKIP_CODE_BADGE = {
  not_pdf: 'Format invalide',
  empty_name: 'Nom invalide',
  coach_not_found: 'Coach absent',
  ambiguous_coach: 'Nom ambigu',
  duplicate: 'Doublon',
}

const emptyForm = {
  name: '',
  matriculeFiscal: '',
  address: '',
  email: '',
  phone: '',
  rib: '',
  idSage: '',
  titre: 'Coach sportif',
  isActive: true,
  fiscalCategory: 'auto_entrepreneur',
  retenue: 0.03,
}

const Coaches = () => {
  const history = useHistory()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()
  const admin = isAdmin()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const initialList = parseCoachesListQuery(location.search)
  const [search, setSearch] = useState(initialList.search)
  const [dossierFilter, setDossierFilter] = useState(initialList.dossierFilter)
  const [statusFilter, setStatusFilter] = useState(initialList.statusFilter)
  const [page, setPage] = useState(initialList.page)
  const [pageSize, setPageSize] = useState(initialList.pageSize)
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)
  const importInputRef = useRef(null)
  const [importingDocs, setImportingDocs] = useState(false)
  const [importDocsProgress, setImportDocsProgress] = useState('')
  const importDocsInputRef = useRef(null)
  const [importDocsPreview, setImportDocsPreview] = useState(null)
  const [importDocsResult, setImportDocsResult] = useState(null)
  const [dedupeScanning, setDedupeScanning] = useState(false)
  const [dedupeCleaning, setDedupeCleaning] = useState(false)
  const [dedupeResult, setDedupeResult] = useState(null)
  const [highlightedCoachId, setHighlightedCoachId] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  const loadCoaches = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await api.fetchCoaches({
        search,
        page,
        limit: pageSize,
        dossier: dossierFilter,
        status: statusFilter,
      })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement coachs')
    } finally {
      setLoading(false)
    }
  }, [search, page, pageSize, dossierFilter, statusFilter])

  useEffect(() => {
    loadCoaches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, pageSize, dossierFilter, statusFilter])

  useEffect(() => {
    setPage(1)
  }, [search, pageSize, dossierFilter, statusFilter])

  useEffect(() => {
    setSelectedIds([])
  }, [page, search, pageSize, dossierFilter, statusFilter])

  useEffect(() => {
    if (location.pathname !== '/coaches' || loading || !data?.coaches) return

    const raw = sessionStorage.getItem(COACHES_LIST_RETURN_KEY)
    if (!raw) return

    try {
      const { scrollY, highlightCoachId } = JSON.parse(raw)
      sessionStorage.removeItem(COACHES_LIST_RETURN_KEY)

      const coachVisible = highlightCoachId
        && data.coaches.some((coach) => coach._id === highlightCoachId)

      if (coachVisible) {
        setHighlightedCoachId(highlightCoachId)
      }

      requestAnimationFrame(() => {
        window.scrollTo({ top: Number(scrollY) || 0, behavior: 'auto' })
      })

      if (!coachVisible) return undefined

      const timer = window.setTimeout(() => setHighlightedCoachId(null), 6000)
      return () => window.clearTimeout(timer)
    } catch {
      sessionStorage.removeItem(COACHES_LIST_RETURN_KEY)
      return undefined
    }
  }, [location.pathname, loading, data])

  useEffect(() => {
    if (location.pathname !== '/coaches') return

    const params = new URLSearchParams(location.search)
    if (params.get('new') === '1') {
      openCreate()
      history.replace(`/coaches${buildCoachesListQuery({ page, pageSize, search, dossierFilter, statusFilter })}`)
      return
    }

    const parsed = parseCoachesListQuery(location.search)
    setPage((current) => (current !== parsed.page ? parsed.page : current))
    setPageSize((current) => (current !== parsed.pageSize ? parsed.pageSize : current))
    setSearch((current) => (current !== parsed.search ? parsed.search : current))
    setDossierFilter((current) => (current !== parsed.dossierFilter ? parsed.dossierFilter : current))
    setStatusFilter((current) => (current !== parsed.statusFilter ? parsed.statusFilter : current))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  useEffect(() => {
    if (location.pathname !== '/coaches') return

    const params = new URLSearchParams(location.search)
    if (params.get('new') === '1') return

    const nextQuery = buildCoachesListQuery({ page, pageSize, search, dossierFilter, statusFilter })
    if (location.search !== nextQuery) {
      history.replace(`/coaches${nextQuery}`)
    }
  }, [page, pageSize, search, dossierFilter, statusFilter, location.pathname, location.search, history])

  const openCreate = () => {
    setForm(emptyForm)
    setModalOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      openSnackbar('Nom requis')
      return
    }
    if (!form.matriculeFiscal.trim()) {
      openSnackbar('Matricule fiscal requis')
      return
    }
    setSaving(true)
    try {
      await api.createCoach(form)
      openSnackbarRef.current('Coach créé')
      setModalOpen(false)
      loadCoaches()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur enregistrement')
    } finally {
      setSaving(false)
    }
  }

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    setImporting(true)
    try {
      const { data: result } = await api.importCoachesExcel(formData)
      openSnackbarRef.current(result.message || 'Import terminé')
      if (result.errorCount > 0) {
        openSnackbarRef.current(`${result.errorCount} ligne(s) ignorée(s) — vérifiez le fichier`)
      }
      loadCoaches()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur import Excel')
    } finally {
      setImporting(false)
    }
  }

  const runBulkDocImport = async (files) => {
    const merged = {
      message: '',
      attachedCount: 0,
      skippedCount: 0,
      attached: [],
      skipped: [],
    }

    const totalBatches = Math.ceil(files.length / DOC_IMPORT_BATCH_SIZE)

    for (let i = 0; i < files.length; i += DOC_IMPORT_BATCH_SIZE) {
      const batchIndex = Math.floor(i / DOC_IMPORT_BATCH_SIZE) + 1
      const batch = files.slice(i, i + DOC_IMPORT_BATCH_SIZE)
      setImportDocsProgress(`Lot ${batchIndex}/${totalBatches}…`)

      const formData = new FormData()
      batch.forEach((file) => formData.append('files', file))

      const { data: result } = await api.importCoachDocumentsBulk(formData)
      merged.attachedCount += result.attachedCount || 0
      merged.skippedCount += result.skippedCount || 0
      merged.attached.push(...(result.attached || []))
      merged.skipped.push(...(result.skipped || []))
    }

    merged.message = `${merged.attachedCount} fichier(s) rattaché(s), ${merged.skippedCount} ignoré(s) sur ${files.length} PDF`
    return merged
  }

  const handleImportDocumentsSelect = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return

    setImportingDocs(true)
    setImportDocsProgress('Analyse des fichiers…')

    try {
      const { data: preview } = await api.previewCoachDocumentsImport(files.map((f) => f.name))
      setImportDocsPreview({ files, ...preview })
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur analyse des fichiers')
    } finally {
      setImportingDocs(false)
      setImportDocsProgress('')
    }
  }

  const handleConfirmImportDocuments = async () => {
    if (!importDocsPreview?.valid?.length) return

    const validNames = new Set(importDocsPreview.valid.map((row) => row.fileName))
    const filesToUpload = importDocsPreview.files.filter((file) => validNames.has(file.name))

    setImportingDocs(true)
    setImportDocsProgress('')
    setImportDocsPreview(null)

    try {
      const merged = await runBulkDocImport(filesToUpload)
      setImportDocsResult(merged)
      openSnackbarRef.current(merged.message)
      loadCoaches()
    } catch (error) {
      const status = error?.response?.status
      const serverMessage = error?.response?.data?.message
      const message = serverMessage
        || (status === 413 ? 'Fichiers trop volumineux — compressez les PDF ou importez moins de fichiers à la fois' : null)
        || 'Erreur import documents'
      openSnackbarRef.current(message)
    } finally {
      setImportingDocs(false)
      setImportDocsProgress('')
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.downloadCoachImportTemplate()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = 'template-coaches.xlsx'
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      openSnackbarRef.current('Erreur téléchargement template')
    }
  }

  const handleScanDuplicateDocuments = async () => {
    setDedupeScanning(true)
    try {
      const { data: result } = await api.scanDuplicateCoachDocuments()
      setDedupeResult(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur vérification des doublons')
    } finally {
      setDedupeScanning(false)
    }
  }

  const handleCleanDuplicateDocuments = async () => {
    if (!dedupeResult?.duplicateGroups) return
    setDedupeCleaning(true)
    try {
      const { data: result } = await api.dedupeDuplicateCoachDocuments()
      setDedupeResult(result)
      openSnackbarRef.current(result.message || 'Doublons supprimés')
      loadCoaches()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression des doublons')
    } finally {
      setDedupeCleaning(false)
    }
  }

  const handleBulkStatus = async (isActive) => {
    if (!selectedIds.length) return
    setUpdatingStatus(true)
    try {
      const { data: result } = await api.bulkUpdateCoachesStatus(selectedIds, isActive)
      openSnackbarRef.current(result.message || 'Statut mis à jour')
      setSelectedIds([])
      loadCoaches()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur mise à jour du statut')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleDelete = async () => {
    if (!confirmModal?.ids?.length) return
    const ids = confirmModal.ids
    setDeleting(true)
    try {
      if (ids.length === 1) {
        await api.deleteCoach(ids[0])
      } else {
        await api.deleteCoachesBulk(ids)
      }
      openSnackbarRef.current(`${ids.length} coach(s) supprimé(s)`)
      setConfirmModal(null)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      const remainingOnPage = (data?.coaches?.length ?? ids.length) - ids.length
      if (remainingOnPage === 0 && page > 1) {
        setPage((p) => p - 1)
      } else {
        loadCoaches()
      }
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCoaches = (ids) => {
    if (!ids.length) return
    setConfirmModal({ ids })
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const openCoachDetail = (coachId) => {
    saveCoachesListReturnState(coachId)
    history.push({
      pathname: `/coaches/${coachId}`,
      state: { coachesListQuery: location.search },
    })
  }

  const handleCoachRowClick = (coachId, event) => {
    if (event.target.closest('button, input, a, label')) return
    openCoachDetail(coachId)
  }

  const handleCoachRowKeyDown = (coachId, event) => {
    if (event.target.closest('button, input, a, label')) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openCoachDetail(coachId)
    }
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  const { coaches = [], pagination, stats } = data || {}
  const totalPages = pagination?.totalPages || 1
  const totalCoaches = pagination?.total || 0
  const showPagination = coaches.length > 0
  const pageIds = coaches.map((c) => c._id)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const deleteCount = confirmModal?.ids?.length ?? 0
  const selectedCoaches = coaches.filter((c) => confirmModal?.ids?.includes(c._id))
  const totalInvoicesInSelection = selectedCoaches.reduce(
    (sum, c) => sum + (c.invoiceCount || 0),
    0
  )

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Référentiel coaches</h1>
          <p>Créez, importez ou modifiez les fiches coaches — synchronisées à chaque import mensuel</p>
        </div>
        <div className={styles.topBarActions}>
          <button type="button" className={styles.secondaryBtn} onClick={handleDownloadTemplate}>
            Template Excel
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls"
            className={styles.hiddenFileInput}
            onChange={handleImportExcel}
          />
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={importing || loading}
            onClick={() => importInputRef.current?.click()}
          >
            {importing ? 'Import…' : 'Importer Excel'}
          </button>
          <input
            ref={importDocsInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className={styles.hiddenFileInput}
            onChange={handleImportDocumentsSelect}
          />
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={importingDocs || loading}
            onClick={() => importDocsInputRef.current?.click()}
          >
            {importingDocs
              ? (importDocsProgress || 'Import PDF…')
              : 'Importer dossiers PDF'}
          </button>
          <button type="button" className={styles.ctaBtn} onClick={openCreate}>
            + Nouveau coach
          </button>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Total coaches</span>
          <span className={styles.kpiValue}>{stats?.totalCoaches || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Factures émises</span>
          <span className={styles.kpiValue}>{stats?.totalInvoices || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Total honoraires</span>
          <span className={styles.kpiValue}>{formatTND(stats?.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net cumulé</span>
          <span className={styles.kpiValue}>{formatTND(stats?.totalNet)} TND</span>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="coach-search">Rechercher</label>
          <input
            id="coach-search"
            type="search"
            className={styles.searchInput}
            placeholder="Nom, MF, email, RIB, ID Sage..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="coach-dossier-filter">Dossier administratif</label>
          <select
            id="coach-dossier-filter"
            className={styles.filterSelect}
            value={dossierFilter}
            onChange={(e) => setDossierFilter(e.target.value)}
          >
            {DOSSIER_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="coach-status-filter">Statut coach</label>
          <select
            id="coach-status-filter"
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className={`${styles.filterGroup} ${styles.filterBarAction}`}>
          <label htmlFor="coach-dedupe-btn">Maintenance dossiers</label>
          <button
            id="coach-dedupe-btn"
            type="button"
            className={styles.secondaryBtn}
            disabled={dedupeScanning || dedupeCleaning || loading}
            onClick={handleScanDuplicateDocuments}
          >
            {dedupeScanning ? 'Vérification…' : 'Vérifier les doublons PDF'}
          </button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Liste des coaches</h2>
            <p className={styles.hint}>
              {pagination?.total || 0} coach(s) enregistré(s).
              Import Excel : Nom complet, Matricule fiscal, Email, Téléphone, RIB…
              {' · '}
              PDF : « Nom coach - patente.pdf » via Importer dossiers PDF.
            </p>
          </div>
          {selectedIds.length > 0 && (
            <div className={styles.panelActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={updatingStatus || loading}
                onClick={() => handleBulkStatus(true)}
              >
                Marquer actif ({selectedIds.length})
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={updatingStatus || loading}
                onClick={() => handleBulkStatus(false)}
              >
                Marquer inactif ({selectedIds.length})
              </button>
              {admin && (
                <button
                  type="button"
                  className={styles.deleteBulkBtn}
                  disabled={deleting || loading}
                  onClick={() => requestDeleteCoaches(selectedIds)}
                >
                  Supprimer ({selectedIds.length})
                </button>
              )}
            </div>
          )}
        </div>

        {coaches.length === 0 ? (
          <p className={styles.empty}>
            Aucun coach enregistré. Créez un coach manuellement, importez le template Excel ou lancez un import mensuel RH.
          </p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th className={styles.checkCol}>
                      <input
                        type="checkbox"
                        aria-label="Tout sélectionner sur cette page"
                        checked={allPageSelected}
                        onChange={toggleSelectAllOnPage}
                      />
                    </th>
                    <th>Coach</th>
                    <th>Statut</th>
                    <th>Catégorie fiscale</th>
                    <th>Retenue</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>RIB</th>
                    <th>Factures</th>
                    <th>Dossier administratif</th>
                    <th>Honoraires cumulés</th>
                    <th>Dernière campagne</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coaches.map((coach) => (
                    <tr
                      key={coach._id}
                      id={`coach-row-${coach._id}`}
                      className={[
                        styles.tableRowClickable,
                        highlightedCoachId === coach._id ? styles.rowHighlighted : '',
                      ].filter(Boolean).join(' ')}
                      onClick={(event) => handleCoachRowClick(coach._id, event)}
                      onKeyDown={(event) => handleCoachRowKeyDown(coach._id, event)}
                      tabIndex={0}
                      role="link"
                      aria-label={`Ouvrir la fiche de ${coach.name}`}
                    >
                      <td
                        className={styles.checkCol}
                        data-label="Sélection"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label={`Sélectionner ${coach.name}`}
                          checked={selectedIds.includes(coach._id)}
                          onChange={() => toggleSelect(coach._id)}
                        />
                      </td>
                      <td data-label="Coach">
                        <div className={styles.coachCell}>
                          <div className={styles.avatar}>
                            {coach.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <span className={styles.coachName}>{coach.name}</span>
                            <span className={styles.coachMf}>{coach.matriculeFiscal}</span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Statut">
                        {coach.isActive !== false ? (
                          <span className={`${styles.dossierBadge} ${styles.statusBadgeActive}`}>Actif</span>
                        ) : (
                          <span className={`${styles.dossierBadge} ${styles.statusBadgeInactive}`}>Inactif</span>
                        )}
                      </td>
                      <td data-label="Catégorie fiscale">
                        {formatFiscalCategoryLabel(coach.fiscalCategory)}
                      </td>
                      <td data-label="Retenue">
                        {isAutoEntrepreneur(coach) ? '—' : formatRetenueLabel(coach.retenue)}
                      </td>
                      <td data-label="Email">{coach.email || '—'}</td>
                      <td data-label="Téléphone">{coach.phone || '—'}</td>
                      <td data-label="RIB">{coach.rib || '—'}</td>
                      <td data-label="Factures">{coach.invoiceCount || 0}</td>
                      <td data-label="Dossier administratif">
                        {coach.dossierStatus === 'complete' ? (
                          <span className={`${styles.dossierBadge} ${styles.dossierBadgeComplete}`}>
                            Renseigné
                            <span className={styles.dossierBadgeCount}>
                              {coach.documentCount} doc.
                            </span>
                          </span>
                        ) : (
                          <span className={`${styles.dossierBadge} ${styles.dossierBadgeMissing}`}>
                            À compléter
                          </span>
                        )}
                      </td>
                      <td data-label="Honoraires cumulés">{formatTND(coach.totalHonoraires)} TND</td>
                      <td data-label="Dernière campagne">{coach.periodLabel || '—'}</td>
                      <td data-label="Actions" onClick={(event) => event.stopPropagation()}>
                        <div className={`${styles.actionCell} action-cell-inner`}>
                          <button
                            type="button"
                            className={styles.tableBtn}
                            onClick={() => openCoachDetail(coach._id)}
                          >
                            Voir fiche
                          </button>
                          {admin && (
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              disabled={deleting}
                              onClick={() => requestDeleteCoaches([coach._id])}
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showPagination && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination?.page || page} sur {totalPages}
                  {' · '}{totalCoaches} coach(s)
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
                <select
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Nombre de coaches par page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && (
        <div className={styles.overlay} onClick={() => !saving && setModalOpen(false)} role="presentation">
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="coach-create-title"
          >
            <h2 id="coach-create-title">Nouveau coach</h2>
            <p className={styles.modalHint}>
              Créez la fiche avant l&apos;import mensuel ou pour compléter le référentiel.
              Le matricule fiscal doit être unique.
            </p>
            <form onSubmit={handleSave}>
              <div className={styles.field}>
                <label htmlFor="coach-create-name">Nom complet *</label>
                <input
                  id="coach-create-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-mf">Matricule fiscal *</label>
                <input
                  id="coach-create-mf"
                  value={form.matriculeFiscal}
                  onChange={(e) => setForm({ ...form, matriculeFiscal: e.target.value })}
                  required
                  placeholder="12007658Y/A/P/000"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-email">Email</label>
                <input
                  id="coach-create-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="coach@california-gym.com"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-phone">Téléphone</label>
                <input
                  id="coach-create-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\s/g, '') })}
                  placeholder="Ex. 20123456"
                  inputMode="tel"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-rib">RIB</label>
                <input
                  id="coach-create-rib"
                  value={form.rib}
                  onChange={(e) => setForm({ ...form, rib: e.target.value.replace(/\s/g, '') })}
                  placeholder="20 chiffres"
                  inputMode="numeric"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-idsage">ID Sage</label>
                <input
                  id="coach-create-idsage"
                  value={form.idSage}
                  onChange={(e) => setForm({ ...form, idSage: e.target.value })}
                  placeholder="Identifiant Sage"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-titre">Titre / fonction</label>
                <input
                  id="coach-create-titre"
                  value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  placeholder="Coach sportif"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-create-address">Adresse</label>
                <textarea
                  id="coach-create-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Adresse complète"
                  rows={3}
                />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label htmlFor="coach-create-fiscal">Catégorie fiscale</label>
                  <select
                    id="coach-create-fiscal"
                    value={form.fiscalCategory}
                    onChange={(e) => setForm({ ...form, fiscalCategory: e.target.value })}
                  >
                    {FISCAL_CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label htmlFor="coach-create-status">Statut</label>
                  <select
                    id="coach-create-status"
                    value={form.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>
              {!isAutoEntrepreneur(form) && (
                <div className={styles.field}>
                  <label htmlFor="coach-create-retenue">Retenue par défaut</label>
                  <select
                    id="coach-create-retenue"
                    value={form.retenue}
                    onChange={(e) => setForm({ ...form, retenue: Number(e.target.value) })}
                  >
                    {RETENUE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Créer le coach'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dedupeResult && (
        <div className={styles.overlay} onClick={() => setDedupeResult(null)} role="presentation">
          <div
            className={`${styles.modal} ${styles.importResultModal}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dedupe-docs-title"
          >
            <h2 id="dedupe-docs-title">Vérification des doublons PDF</h2>
            <p className={styles.modalHint}>{dedupeResult.message}</p>

            {dedupeResult.duplicateGroups > 0 ? (
              <>
                <p className={styles.importFormatHint}>
                  {dedupeResult.duplicateGroups} fichier(s) en double sur{' '}
                  {dedupeResult.deletedCount} exemplaire(s) à retirer.
                  Le document le plus récent est conservé.
                </p>
                <div className={`${styles.importResultBlock} ${styles.importResultErrors}`}>
                  <h3>Doublons détectés ({dedupeResult.duplicateGroups})</h3>
                  <ul className={styles.importResultList}>
                    {dedupeResult.duplicates.slice(0, 15).map((row) => (
                      <li key={`${row.coachId}-${row.fileName}`}>
                        <strong>{row.coachName}</strong>
                        <span className={styles.importResultFile}>{row.fileName}</span>
                        <span className={styles.importResultReason}>
                          {row.removeCount} doublon(s) — conserve la version du{' '}
                          {new Date(row.keptAt).toLocaleDateString('fr-FR')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {dedupeResult.duplicates.length > 15 && (
                    <p className={styles.importResultMore}>
                      + {dedupeResult.duplicates.length - 15} autre(s) coach(s)
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className={`${styles.importResultBlock} ${styles.importResultValid}`}>
                <p>Tous les dossiers sont conformes — un seul exemplaire par nom de fichier.</p>
              </div>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setDedupeResult(null)}
                disabled={dedupeCleaning}
              >
                Fermer
              </button>
              {admin && dedupeResult.duplicateGroups > 0 && dedupeResult.dryRun && (
                <button
                  type="button"
                  className={styles.saveBtn}
                  onClick={handleCleanDuplicateDocuments}
                  disabled={dedupeCleaning}
                >
                  {dedupeCleaning
                    ? 'Suppression…'
                    : `Supprimer ${dedupeResult.deletedCount} doublon(s)`}
                </button>
              )}
              {!admin && dedupeResult.duplicateGroups > 0 && dedupeResult.dryRun && (
                <p className={styles.importFormatHint}>
                  Seul un administrateur peut supprimer les doublons détectés.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {importDocsPreview && (
        <div className={styles.overlay} onClick={() => setImportDocsPreview(null)} role="presentation">
          <div
            className={`${styles.modal} ${styles.importResultModal}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-docs-preview-title"
          >
            <h2 id="import-docs-preview-title">Prévisualisation import PDF</h2>
            <p className={styles.modalHint}>
              {importDocsPreview.validCount} valide(s), {importDocsPreview.invalidCount} ignoré(s)
              {' '}sur {importDocsPreview.totalCount} PDF
            </p>
            <p className={styles.importFormatHint}>
              Format recommandé : <code>NOM COACH - patente.pdf</code>
            </p>

            {importDocsPreview.valid?.length > 0 && (
              <div className={`${styles.importResultBlock} ${styles.importResultValid}`}>
                <h3>Prêts à importer ({importDocsPreview.validCount})</h3>
                <ul className={styles.importResultList}>
                  {importDocsPreview.valid.slice(0, 15).map((row) => (
                    <li key={row.fileName}>
                      <strong>{row.coachName}</strong>
                      {' · '}{row.label}
                      <span className={styles.importResultFile}>{row.fileName}</span>
                      {row.warnings?.map((w) => (
                        <span key={w.code} className={styles.importResultWarning}>{w.reason}</span>
                      ))}
                    </li>
                  ))}
                </ul>
                {importDocsPreview.valid.length > 15 && (
                  <p className={styles.importResultMore}>
                    + {importDocsPreview.valid.length - 15} autre(s) fichier(s)
                  </p>
                )}
              </div>
            )}

            {importDocsPreview.invalid?.length > 0 && (
              <div className={`${styles.importResultBlock} ${styles.importResultErrors}`}>
                <h3>Ignorés ({importDocsPreview.invalidCount})</h3>
                <ul className={styles.importResultList}>
                  {importDocsPreview.invalid.slice(0, 15).map((row) => (
                    <li key={row.fileName}>
                      <span className={`${styles.importReasonBadge} ${styles[`importReason_${row.code}`] || ''}`}>
                        {SKIP_CODE_BADGE[row.code] || 'Ignoré'}
                      </span>
                      <strong>{row.fileName}</strong>
                      <span className={styles.importResultReason}>{row.reason}</span>
                    </li>
                  ))}
                </ul>
                {importDocsPreview.invalid.length > 15 && (
                  <p className={styles.importResultMore}>
                    + {importDocsPreview.invalid.length - 15} autre(s) fichier(s)
                  </p>
                )}
              </div>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setImportDocsPreview(null)}
                disabled={importingDocs}
              >
                Annuler
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleConfirmImportDocuments}
                disabled={importingDocs || !importDocsPreview.validCount}
              >
                {importingDocs
                  ? (importDocsProgress || 'Import…')
                  : `Importer ${importDocsPreview.validCount} fiche(s) valide(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {importDocsResult && (
        <div className={styles.overlay} onClick={() => setImportDocsResult(null)} role="presentation">
          <div
            className={`${styles.modal} ${styles.importResultModal}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-docs-title"
          >
            <h2 id="import-docs-title">Résultat import PDF</h2>
            <p className={styles.modalHint}>{importDocsResult.message}</p>

            {importDocsResult.attached?.length > 0 && (
              <div className={styles.importResultBlock}>
                <h3>Rattachés ({importDocsResult.attachedCount})</h3>
                <ul className={styles.importResultList}>
                  {importDocsResult.attached.slice(0, 15).map((row) => (
                    <li key={`${row.documentId}-${row.fileName}`}>
                      <strong>{row.coachName}</strong>
                      {' · '}{row.label}
                      <span className={styles.importResultFile}>{row.fileName}</span>
                    </li>
                  ))}
                </ul>
                {importDocsResult.attached.length > 15 && (
                  <p className={styles.importResultMore}>
                    + {importDocsResult.attached.length - 15} autre(s) fichier(s)
                  </p>
                )}
              </div>
            )}

            {importDocsResult.skipped?.length > 0 && (
              <div className={`${styles.importResultBlock} ${styles.importResultErrors}`}>
                <h3>Ignorés ({importDocsResult.skippedCount})</h3>
                <ul className={styles.importResultList}>
                  {importDocsResult.skipped.slice(0, 15).map((row) => (
                    <li key={row.fileName}>
                      {row.code && (
                        <span className={`${styles.importReasonBadge} ${styles[`importReason_${row.code}`] || ''}`}>
                          {SKIP_CODE_BADGE[row.code] || 'Ignoré'}
                        </span>
                      )}
                      <strong>{row.fileName}</strong>
                      <span className={styles.importResultReason}>{row.reason}</span>
                    </li>
                  ))}
                </ul>
                {importDocsResult.skipped.length > 15 && (
                  <p className={styles.importResultMore}>
                    + {importDocsResult.skipped.length - 15} autre(s) fichier(s)
                  </p>
                )}
              </div>
            )}

            <div className={styles.modalActions}>
              <button type="button" className={styles.saveBtn} onClick={() => setImportDocsResult(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirmModal)}
        variant="warning"
        title={deleteCount > 1 ? `Supprimer ${deleteCount} coaches ?` : 'Supprimer ce coach ?'}
        message={
          deleteCount > 1
            ? `Vous allez supprimer ${deleteCount} fiches coach du référentiel.`
            : 'Vous allez supprimer cette fiche coach du référentiel.'
        }
        detail={
          totalInvoicesInSelection > 0
            ? `Les ${totalInvoicesInSelection} facture(s) associée(s) restent dans leurs campagnes. Un prochain import peut recréer la fiche.`
            : 'Un prochain import Excel peut recréer automatiquement la fiche coach.'
        }
        confirmLabel={deleting ? 'Suppression…' : 'Supprimer'}
        cancelLabel="Annuler"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setConfirmModal(null)}
      />
    </div>
  )
}

export default Coaches
